import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { teamApi, type Member } from "../lib/team";
import { useAuthStore } from "../store/auth";
import { ApiError } from "../lib/api";

export function TeamPage() {
  const me = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.tenant?.role === "admin");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["team"], queryFn: () => teamApi.list() });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["team"] });
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Team</div>
          <h1 className="stage-title">
            People with access to <em>your books.</em>
          </h1>
          <p className="stage-sub">
            Three role tiers, defined narrowly. Accountants log in with their own credentials —
            same workspace, separate audit trail.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>
        <RoleCard
          name="Admin"
          desc="Full access. Manages team, billing, and period locks. Can approve bills above threshold."
          tone="ink"
        />
        <RoleCard
          name="User"
          desc="Day-to-day operator. Creates invoices, bills, journals; reconciles; runs payroll."
          tone="accent"
        />
        <RoleCard
          name="Read only"
          desc="Read everything, change nothing. For auditors, board members, owners who don't operate."
          tone="paper"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24, alignItems: "start" }}>
        <div className="card card-pad-lg">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div className="stage-eyebrow">Members</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
                Active &amp; pending
              </div>
            </div>
          </div>

          {isLoading ? (
            <div style={{ color: "var(--ink-3)" }}>Loading…</div>
          ) : (
            <>
              <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Person</th>
                    <th style={th}>Role</th>
                    <th style={th}>Last login</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.members.map((m) => (
                    <MemberRow
                      key={m.id}
                      member={m}
                      isMe={m.user_id === me?.id}
                      isAdmin={isAdmin}
                      onChanged={refresh}
                    />
                  ))}
                  {data?.invitations.map((inv) => (
                    <tr key={inv.id} style={{ borderTop: "0.5px solid var(--line)" }}>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="avatar av-3">
                            {inv.email.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{inv.email}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-4)" }}>
                              {inv.relationship_label ?? "Pending invitation"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={td}>
                        <span className="status-tag status-review" style={{
                          background: "var(--gold-soft)", color: "#6E5320",
                          padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 500,
                          textTransform: "uppercase", letterSpacing: "0.04em",
                        }}>
                          Pending · {inv.role}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: 12, color: "var(--ink-4)" }}>—</span>
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {isAdmin && (
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 10px", fontSize: 12 }}
                            onClick={async () => {
                              await teamApi.revokeInvite(inv.id);
                              refresh();
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}
        </div>

        {isAdmin ? (
          <InvitePanel onInvited={refresh} />
        ) : (
          <div className="card card-pad-lg" style={{ background: "var(--paper-2)" }}>
            <div className="stage-eyebrow">Invite</div>
            <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>
              Only admins can invite new members. Ask an admin in your team if you need to add
              someone.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 0",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "12px 0",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
  verticalAlign: "middle",
};

function RoleCard({ name, desc, tone }: { name: string; desc: string; tone: "ink" | "accent" | "paper" }) {
  const styles: Record<string, React.CSSProperties> = {
    ink: { background: "var(--ink)", color: "var(--paper)", borderColor: "var(--ink)" },
    accent: { background: "var(--accent-soft)", color: "var(--accent-deep)", borderColor: "var(--accent)" },
    paper: { background: "var(--paper)", color: "var(--ink-2)", borderColor: "var(--line)" },
  };
  return (
    <div className="card" style={styles[tone]}>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 6 }}>
        {name}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, opacity: tone === "ink" ? 0.85 : 1 }}>{desc}</div>
    </div>
  );
}

function MemberRow({
  member,
  isMe,
  isAdmin,
  onChanged,
}: {
  member: Member;
  isMe: boolean;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const initials = member.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <tr>
      <td style={td}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="avatar av-1">{initials}</div>
          <div>
            <div style={{ fontWeight: 500 }}>
              {member.name} {isMe && <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>· you</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-4)" }}>
              {member.email}
              {member.relationship_label && ` · ${member.relationship_label}`}
            </div>
          </div>
        </div>
      </td>
      <td style={td}>
        {isAdmin && !isMe ? (
          <select
            className="form-input"
            style={{ padding: "5px 8px", fontSize: 12, width: "auto" }}
            value={member.role}
            onChange={async (e) => {
              await teamApi.setRole(member.id, e.target.value as Member["role"]);
              onChanged();
            }}
          >
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="read_only">Read only</option>
          </select>
        ) : (
          <span
            className="status-tag"
            style={{
              background: "var(--accent-soft)",
              color: "var(--accent-deep)",
              padding: "3px 9px",
              borderRadius: 100,
              fontSize: 11,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {member.role}
          </span>
        )}
      </td>
      <td style={td}>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {member.last_login_at ? formatDate(member.last_login_at) : "—"}
        </span>
      </td>
      <td style={{ ...td, textAlign: "right" }}>
        {isAdmin && !isMe && (
          <button
            className="btn btn-ghost"
            style={{ padding: "6px 10px", fontSize: 12 }}
            onClick={async () => {
              if (confirm(`Remove ${member.name} from this workspace?`)) {
                await teamApi.remove(member.id);
                onChanged();
              }
            }}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

function InvitePanel({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Member["role"]>("user");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const inv = await teamApi.invite({ email, role, relationshipLabel: label || undefined });
      setAcceptUrl(inv.accept_url);
      setEmail("");
      setLabel("");
      onInvited();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("That email is already a member or has a pending invite.");
      } else {
        setError("Couldn't send the invite. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card card-pad-lg" style={{ position: "sticky", top: 80 }}>
      <div className="stage-eyebrow">Invite</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 14 }}>
        Add someone new
      </div>
      <form onSubmit={send} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="form-field">
          <label className="form-label">Email</label>
          <input
            className="form-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="form-field">
          <label className="form-label">Role</label>
          <select
            className="form-input"
            value={role}
            onChange={(e) => setRole(e.target.value as Member["role"])}
          >
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="read_only">Read only</option>
          </select>
        </div>
        <div className="form-field">
          <label className="form-label">Relationship label (optional)</label>
          <input
            className="form-input"
            placeholder="Accountant · Keytiqa"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div className="form-help">Helps you remember who's who in the audit log.</div>
        </div>
        {error && <div className="banner banner-error">{error}</div>}
        <button className="btn btn-primary" type="submit" disabled={submitting} style={{ justifyContent: "center" }}>
          {submitting ? "Sending…" : "Send invite"}
        </button>
      </form>

      {acceptUrl && (
        <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--accent-soft)", borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: "var(--accent-deep)", fontWeight: 500, marginBottom: 6 }}>
            Invite created · share this link
          </div>
          <div
            className="mono"
            style={{ fontSize: 11, wordBreak: "break-all", color: "var(--ink-2)" }}
          >
            {window.location.origin}{acceptUrl}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-LK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
